import { RootState } from '@/lib/redux/store';
import { createSelector } from '@reduxjs/toolkit';

// Base selector
const selectMediaManagerState = (state: RootState) => state.mediaManager;

// Media selectors
export const selectMedia = createSelector(
  selectMediaManagerState,
  (mediaManager) => mediaManager.media
);

export const selectTotalCount = createSelector(
  selectMediaManagerState,
  (mediaManager) => mediaManager.totalCount
);

export const selectLoading = createSelector(
  selectMediaManagerState,
  (mediaManager) => mediaManager.loading
);

export const selectError = createSelector(
  selectMediaManagerState,
  (mediaManager) => mediaManager.error
);

export const selectHasNextPage = createSelector(
  selectMediaManagerState,
  (mediaManager) => mediaManager.hasNextPage
);

export const selectCurrentPage = createSelector(
  selectMediaManagerState,
  (mediaManager) => mediaManager.currentPage
);

// Filter selectors
export const selectFilters = createSelector(
  selectMediaManagerState,
  (mediaManager) => mediaManager.filters
);

export const selectMediaTypeFilter = createSelector(
  selectFilters,
  (filters) => filters.mediaType
);

export const selectIdentityFilter = createSelector(
  selectFilters,
  (filters) => filters.identityId
);

export const selectSortBy = createSelector(selectFilters, (filters) => filters.sortBy);

export const selectSortDirection = createSelector(
  selectFilters,
  (filters) => filters.sortDirection
);

export const selectSearchQuery = createSelector(
  selectFilters,
  (filters) => filters.searchQuery
);

// Upload progress selectors
export const selectUploadProgress = createSelector(
  selectMediaManagerState,
  (mediaManager) => mediaManager.uploadProgress
);

export const selectUploadProgressByFileName = (fileName: string) =>
  createSelector(selectUploadProgress, (uploadProgress) => uploadProgress[fileName]);

export const selectActiveUploads = createSelector(
  selectUploadProgress,
  (uploadProgress) =>
    Object.values(uploadProgress).filter(
      (progress) => progress.status === 'uploading' || progress.status === 'pending'
    )
);

export const selectFailedUploads = createSelector(
  selectUploadProgress,
  (uploadProgress) =>
    Object.values(uploadProgress).filter((progress) => progress.status === 'error')
);

// Polling selectors
export const selectPollingMediaIds = createSelector(
  selectMediaManagerState,
  (mediaManager) => mediaManager.pollingMediaIds
);

export const selectPendingMedia = createSelector(selectMedia, (media) =>
  media.filter((item) => item.status === 'PENDING')
);

export const selectPendingMediaIds = createSelector(selectPendingMedia, (pendingMedia) =>
  pendingMedia.map((item) => item.media_id)
);

// Filtered media selectors
export const selectFilteredMedia = createSelector(
  [selectMedia, selectFilters],
  (media, filters) => {
    let filteredMedia = [...media];

    // Filter by media type
    if (filters.mediaType !== 'all') {
      const mediaType = filters.mediaType === 'images' ? 'IMAGE' : 'VIDEO';
      filteredMedia = filteredMedia.filter((item) => item.media_type === mediaType);
    }

    // Filter by identity
    if (filters.identityId) {
      filteredMedia = filteredMedia.filter(
        (item) => item.identity_id === filters.identityId
      );
    }

    // Client-side search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filteredMedia = filteredMedia.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.media_id.toLowerCase().includes(query)
      );
    }

    // Sort media
    filteredMedia.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (filters.sortBy) {
        case 'name':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'media_type':
          aValue = a.media_type;
          bValue = b.media_type;
          break;
        case 'created_date':
        default:
          aValue = new Date(a.created_date).getTime();
          bValue = new Date(b.created_date).getTime();
          break;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return filters.sortDirection === 'asc' ? comparison : -comparison;
      } else {
        const comparison = (aValue as number) - (bValue as number);
        return filters.sortDirection === 'asc' ? comparison : -comparison;
      }
    });

    return filteredMedia;
  }
);

// Media by ID selector
export const selectMediaById = (mediaId: string) =>
  createSelector(selectMedia, (media) => media.find((item) => item.media_id === mediaId));

// Statistics selectors
export const selectMediaStats = createSelector(selectMedia, (media) => {
  const totalImages = media.filter((item) => item.media_type === 'IMAGE').length;
  const totalVideos = media.filter((item) => item.media_type === 'VIDEO').length;
  const totalPending = media.filter((item) => item.status === 'PENDING').length;
  const totalErrors = media.filter((item) => item.status === 'ERROR').length;

  return {
    totalImages,
    totalVideos,
    totalPending,
    totalErrors,
    totalActive: media.filter((item) => item.status === 'ACTIVE').length,
  };
});

// Check if state needs refresh
export const selectNeedsRefresh = createSelector(
  [selectPendingMediaIds, selectPollingMediaIds],
  (pendingIds, pollingIds) => {
    // Check if there are pending media that aren't being polled
    return pendingIds.some((id) => !pollingIds.includes(id));
  }
);
